import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { DotsThree, PencilSimple, Lightning, Trash, PhoneCall, ArrowSquareOut } from "@phosphor-icons/react";
import { LEAD_STATUS_TONE, leadPrimaryKind, websitePrimaryKind, showConvertInMenu } from "./leadPrimary";

// Re-export the pure logic so existing importers keep working.
export { LEAD_STATUS_TONE, leadPrimaryKind, websitePrimaryKind, showConvertInMenu };

export function LeadPrimary({ lead, kind, idp = "lead", touch = false, onContacted, onConvert, onOpenBooking }) {
  // SALES-032 v2: rows use Button size="sm" (the ticket's spec); the Button's
  // own gap-2 spaces the icon, so no mr-* on the icon. Cards keep a 44px target.
  const sizeProp = touch ? undefined : "sm";
  const cls = touch ? "h-11 w-full justify-center text-sm" : "";
  if (kind === "contacted")
    return (
      <Button variant="outline" size={sizeProp} onClick={() => onContacted(lead.id)}
        className={`border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 ${cls}`}
        title="Assign this lead to me and mark it contacted" data-testid={`${idp}-contacted-btn-${lead.id}`}>
        <PhoneCall size={13} />Contacted ✓
      </Button>
    );
  if (kind === "convert")
    return (
      <Button variant="outline" size={sizeProp} onClick={() => onConvert(lead.id)} className={cls}
        title="Create the real booking from this lead" data-testid={`${idp}-convert-${lead.id}`}>
        <Lightning size={13} className="text-primary" />Convert
      </Button>
    );
  if (kind === "open-booking")
    return (
      <Button variant="outline" size={sizeProp} onClick={() => onOpenBooking(lead.converted_booking_id)} className={cls}
        data-testid={`${idp}-open-booking-${lead.id}`}>
        <ArrowSquareOut size={13} className="text-primary" />Open booking
      </Button>
    );
  // none — keep the column width stable on a row; fill the space on a card
  return touch ? <div className="flex-1" /> : null;
}

// The ⋯ menu: Edit lead · Convert to booking (when applicable) · Delete… (admin).
// Built on the shadcn DropdownMenu, which gives keyboard open, arrow-between-items
// and Escape-closes-returns-focus for free.
export function LeadActionsMenu({ lead, idp = "lead", isAdmin, showConvert, onEdit, onConvert, onDelete, touch = false }) {
  const name = lead.customer_name || "this lead";
  const btn = touch ? "h-11 w-11" : "h-8 w-8";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={`rounded-full p-0 shrink-0 ${btn}`}
          aria-label={`More actions for ${name}`} data-testid={`${idp}-more-${lead.id}`}>
          <DotsThree size={18} weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onSelect={() => onEdit(lead)} data-testid={`${idp}-menu-edit-${lead.id}`}>
          <PencilSimple size={15} />Edit lead
        </DropdownMenuItem>
        {showConvert && (
          <DropdownMenuItem onSelect={() => onConvert(lead.id)} data-testid={`${idp}-menu-convert-${lead.id}`}>
            <Lightning size={15} className="text-primary" />Convert to booking
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onDelete(lead.id)} className="text-destructive focus:text-destructive"
              data-testid={`${idp}-menu-delete-${lead.id}`}>
              <Trash size={15} />Delete…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
